import React, { useState, useMemo, useEffect } from 'react';
import {
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  InputAdornment,
} from '@mui/material';
import Modal from '../../../components/Modals/Modal/Modal';
import { Ad } from '../../../types/ads.types';
import { formatCurrency } from '../../../utils/formatters';

interface BulkBudgetModalProps {
  open: boolean;
  selectedAds: Ad[];
  onClose: () => void;
  onConfirm: (adsetBudgets: Record<string, number>) => Promise<void>;
}

interface AdsetBudgetData {
  adsetId: string;
  adsetName: string;
  campaignName: string;
  adsCount: number;
  currentBudget: number;
  newBudget: number;
  totalSpend: number;
  totalRevenue: number;
  roas: number;
}

/**
 * Enhanced BulkBudgetModal - Modal for updating budgets with detailed view
 *
 * Features:
 * - Quick Actions: Set all, Increase by $, Adjust by %
 * - Table showing each adset with editable budgets
 * - Shows campaign, ads count, spend, revenue, ROAS
 * - Individual budget editing per adset
 * - Change calculations ($ and %)
 */
export const BulkBudgetModal: React.FC<BulkBudgetModalProps> = ({
  open,
  selectedAds,
  onClose,
  onConfirm,
}) => {
  // Group ads by adset
  const adsetData = useMemo(() => {
    const adsetMap = new Map<string, AdsetBudgetData>();

    selectedAds.forEach((ad) => {
      if (!adsetMap.has(ad.adsetId)) {
        adsetMap.set(ad.adsetId, {
          adsetId: ad.adsetId,
          adsetName: ad.adsetName,
          campaignName: ad.campaignName,
          adsCount: 0,
          currentBudget: ad.adsetDailyBudget || 0,
          newBudget: ad.adsetDailyBudget || 0,
          totalSpend: 0,
          totalRevenue: 0,
          roas: 0,
        });
      }

      const data = adsetMap.get(ad.adsetId)!;
      data.adsCount += 1;
      data.totalSpend += ad.spend;
      data.totalRevenue += ad.revenue;
      data.roas = data.totalSpend > 0 ? data.totalRevenue / data.totalSpend : 0;
    });

    return Array.from(adsetMap.values());
  }, [selectedAds]);

  const [budgets, setBudgets] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);

  // Quick action states
  const [setAllValue, setSetAllValue] = useState('');
  const [increaseByValue, setIncreaseByValue] = useState('');
  const [adjustByPercent, setAdjustByPercent] = useState('');

  // Reset budgets when modal opens or adsetData changes
  useEffect(() => {
    if (open) {
      const initialBudgets = Object.fromEntries(
        adsetData.map(a => [a.adsetId, a.currentBudget])
      );
      setBudgets(initialBudgets);
      setSetAllValue('');
      setIncreaseByValue('');
      setAdjustByPercent('');
    }
  }, [open, adsetData]);

  const handleBudgetChange = (adsetId: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    setBudgets(prev => ({ ...prev, [adsetId]: numValue }));
  };

  const handleSetAll = () => {
    const value = parseFloat(setAllValue);
    if (!isNaN(value) && value >= 0) {
      const newBudgets: Record<string, number> = {};
      adsetData.forEach(adset => {
        newBudgets[adset.adsetId] = value;
      });
      setBudgets(newBudgets);
      setSetAllValue('');
    }
  };

  const handleIncreaseBy = () => {
    const value = parseFloat(increaseByValue);
    if (!isNaN(value)) {
      const newBudgets: Record<string, number> = {};
      adsetData.forEach(adset => {
        newBudgets[adset.adsetId] = Math.max(0, (budgets[adset.adsetId] || 0) + value);
      });
      setBudgets(newBudgets);
      setIncreaseByValue('');
    }
  };

  const handleAdjustByPercent = () => {
    const percent = parseFloat(adjustByPercent);
    if (!isNaN(percent)) {
      const newBudgets: Record<string, number> = {};
      adsetData.forEach(adset => {
        const current = budgets[adset.adsetId] || 0;
        newBudgets[adset.adsetId] = Math.max(0, current * (1 + percent / 100));
      });
      setBudgets(newBudgets);
      setAdjustByPercent('');
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm(budgets);
      onClose();
    } catch (error) {
      // Error handling done in parent
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const getChangeDollar = (adsetId: string) => {
    const adset = adsetData.find(a => a.adsetId === adsetId);
    if (!adset) return 0;
    return (budgets[adsetId] || 0) - adset.currentBudget;
  };

  const getChangePercent = (adsetId: string) => {
    const adset = adsetData.find(a => a.adsetId === adsetId);
    if (!adset || adset.currentBudget === 0) return 0;
    return ((budgets[adsetId] || 0) - adset.currentBudget) / adset.currentBudget * 100;
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Bulk Budget Update"
      maxWidth={1600}
    >
      {/* Header */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Updating budgets for <strong>{selectedAds.length} ads</strong> across{' '}
        <strong>{adsetData.length} adsets</strong>
      </Typography>

      {/* Quick Actions */}
      <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
          Quick Actions
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {/* Set All */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ minWidth: 120 }}>
              Set all adsets to:
            </Typography>
            <TextField
              size="small"
              type="number"
              value={setAllValue}
              onChange={(e) => setSetAllValue(e.target.value)}
              sx={{ width: 100 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={handleSetAll}
              disabled={!setAllValue}
            >
              Apply to All
            </Button>
          </Box>

          {/* Increase By */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ minWidth: 150 }}>
              Increase all budgets by:
            </Typography>
            <TextField
              size="small"
              type="number"
              value={increaseByValue}
              onChange={(e) => setIncreaseByValue(e.target.value)}
              sx={{ width: 100 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={handleIncreaseBy}
              disabled={!increaseByValue}
            >
              Increase
            </Button>
          </Box>

          {/* Adjust By % */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ minWidth: 140 }}>
              Adjust all budgets by:
            </Typography>
            <TextField
              size="small"
              type="number"
              value={adjustByPercent}
              onChange={(e) => setAdjustByPercent(e.target.value)}
              sx={{ width: 100 }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={handleAdjustByPercent}
              disabled={!adjustByPercent}
            >
              Apply %
            </Button>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Adsets Table */}
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ maxHeight: 500, mb: 3 }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Adset Name</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Campaign</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Ads</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Current Budget</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">New Budget</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Change ($)</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Change %</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Spend</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Revenue</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">ROAS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {adsetData.map((adset) => {
              const changeDollar = getChangeDollar(adset.adsetId);
              const changePercent = getChangePercent(adset.adsetId);

              return (
                <TableRow key={adset.adsetId} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.813rem' }}>
                      {adset.adsetName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.813rem' }}>
                      {adset.campaignName}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{adset.adsCount}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {formatCurrency(adset.currentBudget)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={budgets[adset.adsetId] || 0}
                      onChange={(e) => handleBudgetChange(adset.adsetId, e.target.value)}
                      sx={{ width: 100 }}
                      inputProps={{
                        min: 0,
                        step: 0.01,
                        style: { textAlign: 'right' },
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      sx={{
                        color: changeDollar > 0 ? 'success.main' : changeDollar < 0 ? 'error.main' : 'text.secondary'
                      }}
                    >
                      {changeDollar > 0 ? '+' : ''}{formatCurrency(changeDollar)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      sx={{
                        color: changePercent > 0 ? 'success.main' : changePercent < 0 ? 'error.main' : 'text.secondary'
                      }}
                    >
                      {changePercent > 0 ? '+' : ''}{changePercent.toFixed(1)}%
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {formatCurrency(adset.totalSpend)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {formatCurrency(adset.totalRevenue)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      sx={{
                        color: adset.roas >= 1 ? 'success.main' : 'error.main',
                        fontWeight: 500,
                      }}
                    >
                      {adset.roas.toFixed(2)}x
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={handleClose} disabled={loading} color="inherit" variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="success"
          disabled={loading}
          startIcon={loading && <CircularProgress size={16} />}
        >
          {loading ? 'Updating...' : `Update ${adsetData.length} Adset Budget${adsetData.length !== 1 ? 's' : ''}`}
        </Button>
      </Box>
    </Modal>
  );
};

export default BulkBudgetModal;
